import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Claim } from '@wsphere/warranties/domain';

interface ChecklistRow {
  label: string;
  isDone: boolean;
  hint: string | null;
  /** Takes the user to where the row gets fixed; null when they can't fix it, so the hint stays plain text. */
  fix: (() => void) | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-authorization-card',
  templateUrl: './claim-authorization-card.component.html',
  standalone: false,
})
export class ClaimAuthorizationCardComponent {
  // Computed on input change, not every change detection.
  @Input() set claim(claim: Claim.Model | null) {
    this._claim = claim;
    this.update();
  }
  get claim() {
    return this._claim;
  }
  private _claim: Claim.Model | null = null;

  /** Tenant setting: allow $0 authorizations. */
  @Input() set isZeroPayAllowed(isAllowed: boolean) {
    this._isZeroPayAllowed = isAllowed;
    this.update();
  }
  get isZeroPayAllowed() {
    return this._isZeroPayAllowed;
  }
  private _isZeroPayAllowed = false;

  /** Staff with clm_EditClaim, same as the intake card: without it the loss-information hint isn't a link. */
  @Input() set canEditIntake(canEdit: boolean) {
    this._canEditIntake = canEdit;
    this.update();
  }
  get canEditIntake() {
    return this._canEditIntake;
  }
  private _canEditIntake = false;

  /** Off where the page can't create a claim item: with no job, the job hints stay plain text. */
  @Input() set canCreateItem(canCreate: boolean) {
    this._canCreateItem = canCreate;
    this.update();
  }
  get canCreateItem() {
    return this._canCreateItem;
  }
  private _canCreateItem = true;

  /** Staff with clm_AuthorizeClaim for this claim's distributor. */
  @Input() canAuthorize = false;

  /** Staff with clm_ReverseClaimAuthorization for this claim's distributor. */
  @Input() canReverse = false;

  /** Emits after authorize or reverse, so the page reloads. */
  @Output() changed = new EventEmitter<void>();

  /** The checklist asks for loss information: the page opens the intake dialog. */
  @Output() editIntake = new EventEmitter<void>();

  /** The checklist asks for job work: the page opens that job, or a new one when it's null. */
  @Output() openItem = new EventEmitter<string | null>();

  readonly actions = Claim.Authorization.Action;

  history: Claim.Authorization.Model[] = [];

  /** Every unreversed authorization, oldest first: the first one and its supplementals. */
  authorizations: Claim.Authorization.Model[] = [];

  /** The latest unreversed authorization: the only one that can be reversed. */
  active: Claim.Authorization.Model | null = null;

  /** On an authorized claim, jobs added after a payment, waiting for a supplemental authorization. */
  hasNewJob = false;

  /** What a new authorization would add: everything at first, then only the new jobs. */
  totals: Claim.ClaimTotals | null = null;

  /** Same rules as the server. */
  checklist: ChecklistRow[] = [];
  isReady = false;

  get isOpen() {
    return this._claim?.status === Claim.Status.OPEN;
  }

  get canStart() {
    return this.canAuthorize && this.isOpen && (!this._claim?.frozen || this.hasNewJob);
  }

  private update() {
    const claim = this._claim;
    this.history = this.getHistory(claim);
    this.authorizations = claim?.frozen ? Claim.Authorization.getActiveAuthorizations(this.history) : [];
    this.active = this.authorizations[this.authorizations.length - 1] ?? null;
    this.hasNewJob = !!claim?.frozen && this.getNewJobs(claim).some(job => !job.deleted);
    this.totals = claim?.totals ? Claim.Authorization.getOwnTotals(claim.totals, this.authorizations) : null;
    this.checklist = this.getChecklist(claim);
    this.isReady = this.checklist.every(row => row.isDone);
  }

  balanceOf(authorization: Claim.Authorization.Model) {
    return this._claim?.paymentBalances?.find(balance => balance.authorizationId === authorization.id) ?? null;
  }

  /** Where the authorization's payments stand, for its heading. */
  statusOf(authorization: Claim.Authorization.Model): { text: string; theme: ThemeColor } | null {
    const balance = this.balanceOf(authorization);
    if (!balance) return null;

    if (balance.paid >= balance.authorizedPayable) return { text: 'Fully paid', theme: 'success' };
    // Prepared or issued payments hold the rest, but it isn't paid yet.
    if (balance.remaining <= 0) return { text: 'Fully allocated', theme: 'info' };
    return { text: `${this.formatAmount(balance.remaining, authorization.currency)} left`, theme: 'warn' };
  }

  payeeLabel(record: Claim.Authorization.Model) {
    return Claim.Authorization.describePayee(record);
  }

  formatAmount(amount: number, currency = 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
  }

  private getHistory(claim: Claim.Model | null) {
    return [...(claim?.authorizations ?? [])].sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    );
  }

  /** Jobs no authorization covers yet; on an authorized claim, the ones a supplemental would cover. */
  private getNewJobs(claim: Claim.Model) {
    return (claim.items ?? []).filter(item => !item.authorizationId);
  }

  private getChecklist(claim: Claim.Model | null): ChecklistRow[] {
    if (!claim) return [];

    const issues = Claim.Authorization.getReadinessIssues({
      lossDate: claim.lossDate ?? null,
      usageAtLoss: claim.usageAtLoss ?? null,
      isUsageAtLossRequired: claim.isUsageAtLossRequired ?? true,
      // A supplemental checks only the new jobs and what they add, like the server.
      items: this.getNewJobs(claim),
      totals: this.totals,
      allowZeroPay: this._isZeroPayAllowed,
    });
    const row = (
      label: string,
      fields: Claim.Authorization.ReadinessField[],
      fix: ChecklistRow['fix']
    ): ChecklistRow => {
      const missing = issues.filter(issue => fields.includes(issue.field));
      return { label, isDone: !missing.length, hint: missing.map(issue => issue.message).join(' ') || null, fix };
    };

    // Undecided jobs first, else the first job; charges are added on the job too.
    const jobs = this.getNewJobs(claim).filter(job => !job.deleted);
    const job = jobs.find(job => job.status === Claim.ClaimItem.Status.DRAFT) ?? jobs[0] ?? null;
    const openJob = job || this._canCreateItem ? () => this.openItem.emit(job?.id ?? null) : null;

    return [
      row(
        'Loss information complete',
        ['lossDate', 'usageAtLoss'],
        this._canEditIntake ? () => this.editIntake.emit() : null
      ),
      row('Repair items adjudicated', ['items'], openJob),
      row('Totals calculated', ['totals'], openJob),
    ];
  }
}
